import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

// ============================================================================
// Types
// ============================================================================

export interface TeamMember {
    id: number
    login: string
    email: string
    first_name?: string
    last_name?: string
    role: string
    active: boolean
    is_primary: boolean
    joined_at: string
}

export interface PendingInvitation {
    id: number
    email: string
    first_name?: string
    last_name?: string
    role_name?: string
    invited_at?: string
    expires_at?: string
    status: string
}

// ============================================================================
// Query Keys
// ============================================================================

const teamKeys = {
    members: (orgId: string) => ['teamMembers', orgId] as const,
    invitations: (orgId: string) => ['invitations', orgId] as const,
}

// ============================================================================
// Hooks
// ============================================================================

export const useTeamMembers = (orgId?: string) =>
    useQuery<TeamMember[]>({
        queryKey: teamKeys.members(orgId ?? ''),
        enabled: !!orgId,
        queryFn: async () => {
            const { data } = await apiClient.get(`/api/auth/organizations/${orgId}/members`)
            return data.members ?? data
        },
    })

export const useRoles = () =>
    useQuery({
        queryKey: ['roles'],
        queryFn: async () => {
            const { data } = await apiClient.get('/api/auth/roles')
            return data.roles ?? data
        },
    })

export const useInviteMember = (orgId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload: {
            email: string
            first_name?: string
            last_name?: string
            role?: string
        }) => {
            const { data } = await apiClient.post(
                `/api/auth/organizations/${orgId}/invite`,
                { ...payload, mode: 'invite' }
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.members(orgId) })
            queryClient.invalidateQueries({ queryKey: teamKeys.invitations(orgId) })
        },
    })
}

export const useUpdateMember = (orgId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, role_id }: { id: number; role_id: number }) => {
            const { data } = await apiClient.put(
                `/api/auth/organizations/${orgId}/members/${id}/role`,
                { role_id }
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.members(orgId) })
        },
    })
}

export const useRemoveMember = (orgId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (userId: number) => {
            await apiClient.delete(`/api/auth/organizations/${orgId}/members/${userId}`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.members(orgId) })
        },
    })
}

export const usePendingInvitations = (orgId?: string) =>
    useQuery<PendingInvitation[]>({
        queryKey: teamKeys.invitations(orgId ?? ''),
        enabled: !!orgId,
        queryFn: async () => {
            const { data } = await apiClient.get(
                `/api/auth/organizations/${orgId}/invitations?status_filter=pending`
            )
            return data.invitations ?? data
        },
    })

export const useCancelInvitation = (orgId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (invitationId: number) => {
            await apiClient.delete(
                `/api/auth/organizations/${orgId}/invitations/${invitationId}`
            )
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.invitations(orgId) })
        },
    })
}

export const useResendInvitation = (orgId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (invitationId: number) => {
            const { data } = await apiClient.post(
                `/api/auth/organizations/${orgId}/invitations/${invitationId}/resend`,
                {}
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamKeys.invitations(orgId) })
        },
    })
}